/**
 * ObfuscatedEmail — display an email address that resists automated scraping
 * while remaining fully usable (click-to-mail) for human visitors.
 *
 * How it works:
 *   • Server-rendered / initial HTML shows a bot-friendly plain text
 *     "user [at] domain" without the `@` symbol and without any `mailto:`
 *     link. Most scrapers grep for `@` or `mailto:` — they see nothing.
 *   • After React hydration (useEffect), the component replaces itself
 *     with the actual clickable `<a href="mailto:…">`. Humans see the same
 *     email as before, one-click to open their client.
 *
 * Rationale: `contact@drone-maps.eu` used to appear in ≥8 pages + JSON-LD,
 * which drove spam+phishing volume. This drops the harvestable surface to
 * zero without removing legal accessibility (RGPD, LCEN mentions légales).
 */
import { useEffect, useState } from 'react';

export function ObfuscatedEmail({
  user = 'contact',
  domain = 'drone-maps.eu',
  className = '',
  linkClassName = '',
}) {
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    // Delay a tick so the initial static HTML captured by scrapers
    // (curl, wget, headless crawlers without JS execution) never
    // contains the `@` glyph.
    setReveal(true);
  }, []);

  if (!reveal) {
    return (
      <span className={className} data-testid="obfuscated-email">
        {user} [at] {domain}
      </span>
    );
  }

  const email = `${user}@${domain}`;
  return (
    <a
      href={`mailto:${email}`}
      className={linkClassName || className}
      data-testid="obfuscated-email-link"
    >
      {email}
    </a>
  );
}

export default ObfuscatedEmail;
